import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

// Google Sheets API credentials from environment variables
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || "";
const SHEET_ID = import.meta.env.VITE_GOOGLE_SHEET_ID || "";

export interface Member {
  id: string;
  name: string;
  nameE?: string;
  age: number;
  sex: string;
  role: string;
  occupation: string;
  currentResidence: string;
  education?: string;
  maternal?: string;
  contact?: string;
  dob?: string;
  familyId?: string;
  villageId?: string;
}

export interface Family {
  id: string;
  familyName: string;
  members: Member[];
}

export interface Village {
  id: string;
  villageName: string;
  families: Family[];
}

export const useVillagesStore = defineStore('villages', () => {
  const villages = ref<Village[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const searchQuery = ref('');
  const homeSignal = ref(0);

  const totalVillages = computed(() => villages.value.length);

  const totalFamilies = computed(() =>
    villages.value.reduce((acc, v) => acc + (v.families?.length ?? 0), 0)
  );

  const totalPeople = computed(() =>
    villages.value.reduce(
      (acc, v) =>
        acc + (v.families?.reduce((a: number, f: any) => a + (f.members?.length ?? 0), 0) ?? 0),
      0
    )
  );

  // Quick lookup list of all members
  const allMembers = computed(() => {
    const list: (Member & { villageName: string; familyName: string })[] = [];
    villages.value.forEach((v) => {
      (v.families ?? []).forEach((fam) => {
        (fam.members ?? []).forEach((mem) => {
          list.push({
            ...mem,
            villageName: v.villageName,
            familyName: fam.familyName,
          });
        });
      });
    });
    return list;
  });

  const searchResults = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    const results: any[] = [];

    villages.value.forEach((v) => {
      const villageName = v.villageName ?? '';
      (v.families ?? []).forEach((fam: any) => {
        const familyName = fam.familyName ?? '';
        const headMember =
          (fam.members ?? []).find((m: any) => ['head', 'self'].includes((m.role || '').toLowerCase())) || (fam.members ?? [])[0];
        const headName = headMember?.name ?? '';

        (fam.members ?? []).forEach((mem: any) => {
          const haystack = [
            villageName,
            familyName,
            mem.name,
            mem.nameE,
            mem.role,
            mem.occupation,
            mem.currentResidence,
            mem.education,
            mem.maternal,
            mem.contact,
            mem.sex,
            mem.age,
            mem.dob,
          ]
            .filter((x) => x !== undefined && x !== null)
            .map((x) => String(x).toLowerCase())
            .join(' | ');

          const matches = tokens.every((t) => haystack.includes(t));
          if (matches) {
            results.push({
              villageName,
              villageId: v.id,
              familyName,
              headName,
              familyId: fam.id,
              member: mem,
            });
          }
        });
      });
    });

    return results;
  });

  function setVillages(newVillages: Village[]) {
    villages.value = newVillages;
  }

  function applyMemberUpdate(villageId: string, familyId: string, memberId: string, updatedFields: Partial<Member>) {
    const village = villages.value.find((v) => v.id === villageId);
    if (!village) return;
    const family = village.families?.find((f) => f.id === familyId);
    if (!family) return;
    const member = family.members?.find((m) => m.id === memberId);
    if (member) {
      Object.assign(member, updatedFields);
    }
  }

  function setSearchQuery(q: string) {
    searchQuery.value = q;
  }

  function clearSearch() {
    searchQuery.value = '';
  }

  function requestHome() {
    homeSignal.value += 1;
  }

  function cleanPhone(phone: any): string {
    if (!phone) return '';
    return String(phone).replace(/\D/g, '').slice(-10);
  }

  function extractAllPhones(raw: any): string[] {
    if (!raw) return [];
    const str = String(raw);
    const matches = str.match(/(?:\+?91[\s-]*)?([6-9]\d{9})/g) || [];
    const phones = matches.map((m) => m.replace(/\D/g, '').slice(-10));
    const directClean = cleanPhone(str);
    if (directClean.length === 10 && !phones.includes(directClean)) {
      phones.push(directClean);
    }
    return phones;
  }

  function findMemberByPhone(phone: string) {
    const target = cleanPhone(phone);
    if (!target || target.length < 10) return null;

    for (const mem of allMembers.value) {
      const activeContact = mem.contact || '';
      const contactPhones = extractAllPhones(activeContact);
      if (contactPhones.includes(target) || cleanPhone(activeContact) === target) {
        return mem;
      }
      const occPhones = extractAllPhones(mem.occupation);
      if (occPhones.includes(target)) {
        return mem;
      }
    }
    return null;
  }

  function groupByVillageAndFamily(rows: any[][], headers: string[]) {
    const result: Record<string, any> = {};
    let memberCounter = 1;

    rows.forEach((row) => {
      const person: Record<string, string> = {};
      headers.forEach((h, i) => {
        if (h) {
          person[h.trim()] = row[i] ? String(row[i]).trim() : '';
        }
      });

      const getVal = (...keys: string[]) => {
        for (const k of keys) {
          const lower = k.toLowerCase().replace(/[^a-z0-9]/g, '');
          for (const [pk, pv] of Object.entries(person)) {
            if (pk.toLowerCase().replace(/[^a-z0-9]/g, '') === lower && pv !== undefined && pv !== null && String(pv).trim() !== '') {
              return String(pv).trim();
            }
          }
        }
        return '';
      };

      const villageId = getVal('Village ID', 'Village Id', 'VillageID') || '1';
      const villageName = getVal('Village Name', 'Village Name E') || 'ગામ';
      const familyId = getVal('Family ID') || '1';

      if (!result[villageId]) {
        result[villageId] = { __villageName: villageName };
      }

      if (!result[villageId][familyId]) {
        result[villageId][familyId] = {
          familyName: getVal('Family Name', 'Name') || `Family ${familyId}`,
          members: [],
        };
      }

      const memId = `mem-${memberCounter++}`;
      const rawContact = getVal('Occupation contact', 'Contact', 'Mobile', 'Phone', 'Contact No', 'Mobile No');

      result[villageId][familyId].members.push({
        id: memId,
        name: getVal('Name'),
        nameE: getVal('Name E'),
        age: Number(getVal('Age')) || 0,
        sex: getVal('Gender', 'Sex') || 'Male',
        role: getVal('Role') || 'Self',
        occupation: getVal('Occupation', 'Occupation E'),
        currentResidence: getVal('Residence', 'Residence E', 'Current Residence'),
        education: getVal('Eduction', 'Education'),
        maternal: getVal('Maternal', 'Maternal E'),
        contact: rawContact,
        dob: getVal('Date of Birth'),
        villageId: String(villageId),
        familyId: String(familyId),
      });
    });

    return result;
  }

  async function fetchVillagesFromSheet(force = false) {
    if (villages.value.length > 0 && !force) {
      return;
    }
    if (loading.value) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const apiKey = import.meta.env.VITE_GOOGLE_API_KEY || API_KEY;
      const sheetId = import.meta.env.VITE_GOOGLE_SHEET_ID || SHEET_ID;

      if (!apiKey || !sheetId) {
        throw new Error('Google Sheet credentials are not configured.');
      }

      const res = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/Sheet1!A1:Z5000?key=${apiKey}`
      );
      const json = await res.json();

      if (!res.ok || json.error) {
        const errMsg = json?.error?.message || `Google Sheets API Error (HTTP ${res.status})`;
        console.error('Google Sheets API Error Details:', json?.error);
        throw new Error(errMsg);
      }

      if (!json.values || json.values.length === 0) {
        throw new Error('No data returned from Google Sheet');
      }

      const [headers, ...rows] = json.values;

      // Group by village and family
      const grouped = groupByVillageAndFamily(rows, headers);

      // Transform to expected format
      const villagesData: Village[] = Object.entries(grouped).map(([villageId, families]) => {
        const familiesObj = families as Record<string, any>;
        const villageName = familiesObj.__villageName || '';
        const cleanedFamilies: Family[] = Object.entries(familiesObj)
          .filter(([key]) => key !== '__villageName')
          .map(([familyId, familyData]: any) => ({
            id: String(familyId),
            familyName: familyData.familyName,
            members: familyData.members,
          }));
        return {
          id: String(villageId),
          villageName,
          families: cleanedFamilies,
        };
      });

      setVillages(villagesData);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Error fetching villages:', err);
    } finally {
      loading.value = false;
    }
  }

  function getVillageById(id: string) {
    return villages.value.find((v) => v.id === id) ?? null;
  }

  return {
    villages,
    loading,
    error,
    searchQuery,
    homeSignal,
    totalVillages,
    totalFamilies,
    totalPeople,
    allMembers,
    searchResults,
    setVillages,
    setSearchQuery,
    clearSearch,
    requestHome,
    cleanPhone,
    findMemberByPhone,
    fetchVillagesFromSheet,
    getVillageById,
    applyMemberUpdate,
  };
});
